import React, { useState, useEffect, useMemo } from 'react';
import {
  User, Bell, Shield, BookOpen, Save, Loader2, CheckCircle,
  Mail, Globe, Moon, Sun, Monitor, Target, Video, Headphones,
  FileText, Layers, Eye, EyeOff, LogOut, Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AuthApi } from '../../services/api';
import StudentPortalTabHeader from './StudentPortalTabHeader';
import './StudentPortalSettings.css';

const SECTIONS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'learning', label: 'Learning', icon: BookOpen },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
];

const INDUSTRIES = [
  'technology', 'manufacturing', 'energy', 'automotive', 'aerospace',
  'healthcare', 'education', 'consulting', 'finance', 'sustainability',
  'research', 'government', 'startup', 'other',
];

const INTERESTS = [
  'artificial-intelligence', 'machine-learning', 'sustainability', 'renewable-energy',
  'manufacturing', 'automation', 'robotics', 'data-science', 'climate-tech',
  'green-manufacturing', 'environmental-engineering',
];

const DEFAULT_PREFS = {
  notifications: {
    email: { courseUpdates: true, newCourses: true, achievements: true, reminders: true },
    push: { courseUpdates: false, newCourses: false, achievements: true, reminders: true },
  },
  language: 'en',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  theme: 'dark',
  learning: { preferredFormat: 'mixed', learningStyle: 'balanced', weeklyGoalHours: 5 },
};

const formatLabel = (s = '') =>
  s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const StudentPortalSettings = ({ profile, token, onProfileUpdated, onLogout }) => {
  const [section, setSection] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  const [form, setForm] = useState({
    name: '',
    bio: '',
    phone: '',
    company: '',
    jobTitle: '',
    industry: '',
    interests: [],
    preferences: DEFAULT_PREFS,
  });

  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (!profile) return;
    const prefs = profile.preferences || {};
    setForm({
      name: profile.name || '',
      bio: profile.bio || '',
      phone: profile.phone || '',
      company: profile.company || '',
      jobTitle: profile.jobTitle || '',
      industry: profile.industry || '',
      interests: profile.interests || [],
      preferences: {
        ...DEFAULT_PREFS,
        ...prefs,
        notifications: {
          email: { ...DEFAULT_PREFS.notifications.email, ...prefs.notifications?.email },
          push: { ...DEFAULT_PREFS.notifications.push, ...prefs.notifications?.push },
        },
        learning: { ...DEFAULT_PREFS.learning, ...prefs.learning },
      },
    });
  }, [profile]);

  const avatar = useMemo(() => {
    const n = form.name || profile?.email || 'S U';
    return n.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();
  }, [form.name, profile?.email]);

  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : null;

  const setPref = (path, value) => {
    setForm((prev) => {
      const next = { ...prev, preferences: { ...prev.preferences } };
      const parts = path.split('.');
      let cur = next.preferences;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const toggleInterest = (id) => {
    setForm((prev) => {
      const has = prev.interests.includes(id);
      return {
        ...prev,
        interests: has ? prev.interests.filter((i) => i !== id) : [...prev.interests, id],
      };
    });
  };

  const handleSaveProfile = async () => {
    if (!token) return;
    setSaving(true);
    try {
      const res = await AuthApi.updateMe(token, {
        name: form.name.trim(),
        bio: form.bio.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        jobTitle: form.jobTitle.trim(),
        industry: form.industry || undefined,
        interests: form.interests,
        preferences: form.preferences,
      });
      const updated = res.data?.user || res.user;
      if (updated) onProfileUpdated?.(updated);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (!token) return;
    setPwdSaving(true);
    try {
      await AuthApi.changePassword(token, {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      toast.success('Password updated');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setPwdSaving(false);
    }
  };

  const Toggle = ({ checked, onChange, label, hint }) => (
    <label className="sp-settings-toggle">
      <div className="sp-settings-toggle__text">
        <span>{label}</span>
        {hint && <small>{hint}</small>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`sp-settings-switch ${checked ? 'sp-settings-switch--on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className="sp-settings-switch__thumb" />
      </button>
    </label>
  );

  return (
    <div className="tab-content sp-settings">
      <StudentPortalTabHeader
        icon={<User size={20} />}
        title="Settings"
        subtitle="Manage your profile, learning preferences, notifications, and account security."
        actions={(
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSaveProfile}
            disabled={saving}
          >
            {saving ? <Loader2 size={16} className="sp-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        )}
      />

      <div className="sp-settings__layout">
        <nav className="sp-settings-nav" aria-label="Settings sections">
          {SECTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`sp-settings-nav__item ${section === id ? 'sp-settings-nav__item--active' : ''}`}
              onClick={() => setSection(id)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="sp-settings__panel">
          {section === 'profile' && (
            <div className="sp-settings-section">
              <div className="portal-card sp-settings-hero">
                <div className="sp-settings-hero__avatar">{avatar}</div>
                <div className="sp-settings-hero__info">
                  <h3>{form.name || 'Your profile'}</h3>
                  <p>{profile?.email}</p>
                  <div className="sp-settings-hero__meta">
                    {profile?.role && <span className="sp-settings-chip">{formatLabel(profile.role)}</span>}
                    {memberSince && <span className="sp-settings-chip">Member since {memberSince}</span>}
                    {profile?.emailVerified && (
                      <span className="sp-settings-chip sp-settings-chip--success">
                        <CheckCircle size={12} /> Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="portal-card sp-settings-card">
                <h4 className="sp-settings-card__title">Personal information</h4>
                <div className="sp-settings-grid">
                  <label className="sp-settings-field sp-settings-field--full">
                    <span>Display name</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Your name"
                    />
                  </label>
                  <label className="sp-settings-field sp-settings-field--full">
                    <span>Bio</span>
                    <textarea
                      rows={3}
                      value={form.bio}
                      onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
                      placeholder="A short intro for mentors and peers…"
                      maxLength={500}
                    />
                  </label>
                  <label className="sp-settings-field">
                    <span>Phone</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="Optional"
                    />
                  </label>
                  <label className="sp-settings-field">
                    <span>Industry</span>
                    <select
                      value={form.industry}
                      onChange={(e) => setForm((p) => ({ ...p, industry: e.target.value }))}
                    >
                      <option value="">Select industry</option>
                      {INDUSTRIES.map((ind) => (
                        <option key={ind} value={ind}>{formatLabel(ind)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="sp-settings-field">
                    <span>Company</span>
                    <input
                      type="text"
                      value={form.company}
                      onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))}
                      placeholder="Where you work or study"
                    />
                  </label>
                  <label className="sp-settings-field">
                    <span>Job title</span>
                    <input
                      type="text"
                      value={form.jobTitle}
                      onChange={(e) => setForm((p) => ({ ...p, jobTitle: e.target.value }))}
                      placeholder="e.g. Engineering student"
                    />
                  </label>
                </div>
              </div>

              <div className="portal-card sp-settings-card">
                <h4 className="sp-settings-card__title">Interests</h4>
                <p className="sp-settings-card__desc">Helps personalize your learning path and course recommendations.</p>
                <div className="sp-settings-tags">
                  {INTERESTS.map((id) => (
                    <button
                      key={id}
                      type="button"
                      className={`sp-settings-tag ${form.interests.includes(id) ? 'sp-settings-tag--active' : ''}`}
                      onClick={() => toggleInterest(id)}
                    >
                      {formatLabel(id)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section === 'learning' && (
            <div className="sp-settings-section">
              <div className="portal-card sp-settings-card">
                <h4 className="sp-settings-card__title">
                  <Sparkles size={18} />
                  Learning preferences
                </h4>
                <p className="sp-settings-card__desc">
                  These settings influence AI-generated lesson formats and your path recommendations.
                </p>

                <div className="sp-settings-format-grid">
                  {[
                    { id: 'mixed', icon: Layers, label: 'Mixed', desc: 'Best of all formats' },
                    { id: 'text', icon: FileText, label: 'Reading', desc: 'Text & diagrams' },
                    { id: 'video', icon: Video, label: 'Video', desc: 'Visual lessons' },
                    { id: 'audio', icon: Headphones, label: 'Audio', desc: 'Listen on the go' },
                  ].map(({ id, icon: Icon, label, desc }) => (
                    <button
                      key={id}
                      type="button"
                      className={`sp-settings-format ${form.preferences.learning.preferredFormat === id ? 'sp-settings-format--active' : ''}`}
                      onClick={() => setPref('learning.preferredFormat', id)}
                    >
                      <Icon size={22} />
                      <strong>{label}</strong>
                      <small>{desc}</small>
                    </button>
                  ))}
                </div>

                <label className="sp-settings-field">
                  <span>Learning style</span>
                  <select
                    value={form.preferences.learning.learningStyle}
                    onChange={(e) => setPref('learning.learningStyle', e.target.value)}
                  >
                    <option value="balanced">Balanced</option>
                    <option value="visual">Visual — diagrams & demos</option>
                    <option value="reading">Reading — deep text focus</option>
                    <option value="practical">Practical — hands-on first</option>
                  </select>
                </label>

                <label className="sp-settings-field">
                  <span>Weekly learning goal</span>
                  <div className="sp-settings-range">
                    <input
                      type="range"
                      min={1}
                      max={20}
                      value={form.preferences.learning.weeklyGoalHours}
                      onChange={(e) => setPref('learning.weeklyGoalHours', Number(e.target.value))}
                    />
                    <strong>{form.preferences.learning.weeklyGoalHours}h / week</strong>
                  </div>
                </label>
              </div>

              <div className="portal-card sp-settings-card">
                <h4 className="sp-settings-card__title">
                  <Globe size={18} />
                  Regional & display
                </h4>
                <div className="sp-settings-grid">
                  <label className="sp-settings-field">
                    <span>Language</span>
                    <select
                      value={form.preferences.language}
                      onChange={(e) => setPref('language', e.target.value)}
                    >
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="fr">Français</option>
                      <option value="de">Deutsch</option>
                      <option value="zh">中文</option>
                      <option value="ja">日本語</option>
                    </select>
                  </label>
                  <label className="sp-settings-field">
                    <span>Timezone</span>
                    <input
                      type="text"
                      value={form.preferences.timezone}
                      onChange={(e) => setPref('timezone', e.target.value)}
                    />
                  </label>
                </div>

                <div className="sp-settings-theme">
                  {[
                    { id: 'dark', icon: Moon, label: 'Dark' },
                    { id: 'light', icon: Sun, label: 'Light' },
                    { id: 'auto', icon: Monitor, label: 'System' },
                  ].map(({ id, icon: Icon, label }) => (
                    <button
                      key={id}
                      type="button"
                      className={`sp-settings-theme__btn ${form.preferences.theme === id ? 'sp-settings-theme__btn--active' : ''}`}
                      onClick={() => setPref('theme', id)}
                    >
                      <Icon size={18} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section === 'notifications' && (
            <div className="sp-settings-section">
              <div className="portal-card sp-settings-card">
                <h4 className="sp-settings-card__title">
                  <Mail size={18} />
                  Email notifications
                </h4>
                <div className="sp-settings-toggles">
                  <Toggle
                    label="Course updates"
                    hint="Progress, new lessons, instructor announcements"
                    checked={form.preferences.notifications.email.courseUpdates}
                    onChange={(v) => setPref('notifications.email.courseUpdates', v)}
                  />
                  <Toggle
                    label="New courses"
                    hint="Recommendations and catalog launches"
                    checked={form.preferences.notifications.email.newCourses}
                    onChange={(v) => setPref('notifications.email.newCourses', v)}
                  />
                  <Toggle
                    label="Achievements"
                    hint="Badges, certificates, milestones"
                    checked={form.preferences.notifications.email.achievements}
                    onChange={(v) => setPref('notifications.email.achievements', v)}
                  />
                  <Toggle
                    label="Study reminders"
                    hint="Gentle nudges to keep your streak"
                    checked={form.preferences.notifications.email.reminders}
                    onChange={(v) => setPref('notifications.email.reminders', v)}
                  />
                </div>
              </div>

              <div className="portal-card sp-settings-card">
                <h4 className="sp-settings-card__title">
                  <Bell size={18} />
                  Push notifications
                </h4>
                <div className="sp-settings-toggles">
                  <Toggle
                    label="Course updates"
                    checked={form.preferences.notifications.push.courseUpdates}
                    onChange={(v) => setPref('notifications.push.courseUpdates', v)}
                  />
                  <Toggle
                    label="New courses"
                    checked={form.preferences.notifications.push.newCourses}
                    onChange={(v) => setPref('notifications.push.newCourses', v)}
                  />
                  <Toggle
                    label="Achievements"
                    checked={form.preferences.notifications.push.achievements}
                    onChange={(v) => setPref('notifications.push.achievements', v)}
                  />
                  <Toggle
                    label="Study reminders"
                    checked={form.preferences.notifications.push.reminders}
                    onChange={(v) => setPref('notifications.push.reminders', v)}
                  />
                </div>
              </div>
            </div>
          )}

          {section === 'security' && (
            <div className="sp-settings-section">
              <form className="portal-card sp-settings-card" onSubmit={handleChangePassword}>
                <h4 className="sp-settings-card__title">
                  <Shield size={18} />
                  Change password
                </h4>
                <p className="sp-settings-card__desc">
                  Use at least 8 characters with uppercase, lowercase, a number, and a special character.
                </p>
                <div className="sp-settings-grid sp-settings-grid--single">
                  <label className="sp-settings-field">
                    <span>Current password</span>
                    <div className="sp-settings-pwd">
                      <input
                        type={showCurrentPwd ? 'text' : 'password'}
                        value={passwords.currentPassword}
                        onChange={(e) => setPasswords((p) => ({ ...p, currentPassword: e.target.value }))}
                        autoComplete="current-password"
                      />
                      <button type="button" className="sp-settings-pwd__toggle" onClick={() => setShowCurrentPwd((v) => !v)}>
                        {showCurrentPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </label>
                  <label className="sp-settings-field">
                    <span>New password</span>
                    <div className="sp-settings-pwd">
                      <input
                        type={showNewPwd ? 'text' : 'password'}
                        value={passwords.newPassword}
                        onChange={(e) => setPasswords((p) => ({ ...p, newPassword: e.target.value }))}
                        autoComplete="new-password"
                      />
                      <button type="button" className="sp-settings-pwd__toggle" onClick={() => setShowNewPwd((v) => !v)}>
                        {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </label>
                  <label className="sp-settings-field">
                    <span>Confirm new password</span>
                    <input
                      type="password"
                      value={passwords.confirmPassword}
                      onChange={(e) => setPasswords((p) => ({ ...p, confirmPassword: e.target.value }))}
                      autoComplete="new-password"
                    />
                  </label>
                </div>
                <button type="submit" className="btn btn-primary" disabled={pwdSaving}>
                  {pwdSaving ? <Loader2 size={16} className="sp-spin" /> : <Shield size={16} />}
                  {pwdSaving ? 'Updating…' : 'Update password'}
                </button>
              </form>

              <div className="portal-card sp-settings-card sp-settings-danger">
                <h4 className="sp-settings-card__title">Session</h4>
                <p className="sp-settings-card__desc">Sign out of this device. Your progress is saved in the cloud.</p>
                <button type="button" className="btn btn-secondary" onClick={onLogout}>
                  <LogOut size={16} />
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentPortalSettings;